import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import ShareLink from '../components/ShareLink';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePlanGate, planGateFromResponse } from '../lib/planGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import { STANDARD_INDUSTRIES, getIndustryLabel } from '../constants/industries';
import { 
  Building2, Briefcase, Target, Trophy, XCircle, Plus, MessageSquare,
  CheckCircle, CheckCheck, Star, User, 
  LayoutGrid, List, BarChart3,
  ChevronDown, DollarSign, TrendingUp,
  Users, Activity, RefreshCw, Search, Percent, Globe, AlertCircle,
  Trash2, Save, Calendar, Clock, FileCheck, FileText, Hash, ArrowRight, ChevronRight, ArrowLeft,
  Download, FileJson, FileSpreadsheet, Bell, Mail, Smartphone, Loader2, GitBranch, ExternalLink
} from 'lucide-react';
import { DialogDescription } from '../components/ui/dialog';
import { format, isPast, isToday, differenceInDays } from 'date-fns';
import { useOnboarding, OnboardingEmptyState } from '../onboarding';

// WO-33 — the opportunity card's "Set Alert" created a TENDER alert with every
// filter empty (the opportunity it named was dropped by the server), i.e. an
// alert that emailed every new tender. No opportunity watch exists behind it,
// so the button is withdrawn; the server now refuses alerts without criteria.
const OPPORTUNITY_WATCH_SUPPORTED = false;

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Guard against NaN/null/undefined when rendering opportunity values (P2-04).
// WO-17 removed a hardcoded `$` because it stated a currency nobody had entered.
// WO-18 goes the rest of the way: an opportunity DOES have a `currency` field
// (routers/feeds.py), it is just usually empty. So show the stored currency when
// there is one and a bare number when there is not -- never an invented symbol.
// Same rule as the alert emails: state only what is known.
const toMillions = (value, currency) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return null;
  const amount = `${(num / 1000000).toFixed(1)}M`;
  const code = (currency || '').trim().toUpperCase();
  return code ? `${code} ${amount}` : amount;
};

const countryFlags = {
  SA: '\u{1F1F8}\u{1F1E6}', AE: '\u{1F1E6}\u{1F1EA}', OM: '\u{1F1F4}\u{1F1F2}', 
  KW: '\u{1F1F0}\u{1F1FC}', BH: '\u{1F1E7}\u{1F1ED}', QA: '\u{1F1F6}\u{1F1E6}'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: '\u0627\u0644\u0633\u0639\u0648\u062f\u064a\u0629', AE: '\u0627\u0644\u0625\u0645\u0627\u0631\u0627\u062a', OM: '\u0639\u0645\u0627\u0646', KW: '\u0627\u0644\u0643\u0648\u064a\u062a', BH: '\u0627\u0644\u0628\u062d\u0631\u064a\u0646', QA: '\u0642\u0637\u0631' }
};

// Pipeline stages
const STAGES = [
  { id: 'new', label: 'New', labelAr: '\u062c\u062f\u064a\u062f', color: '#6366f1', icon: Plus },
  { id: 'contacted', label: 'Contacted', labelAr: '\u062a\u0645 \u0627\u0644\u062a\u0648\u0627\u0635\u0644', color: '#f59e0b', icon: MessageSquare },
  { id: 'qualified', label: 'Qualified', labelAr: '\u0645\u0624\u0647\u0644', color: '#3b82f6', icon: CheckCircle },
  { id: 'opportunity', label: 'Opportunity', labelAr: '\u0641\u0631\u0635\u0629', color: '#8b5cf6', icon: Target },
  { id: 'submitted', label: 'Submitted', labelAr: '\u062a\u0645 \u0627\u0644\u062a\u0642\u062f\u064a\u0645', color: '#06b6d4', icon: CheckCheck },
  { id: 'won', label: 'Won', labelAr: '\u0641\u0648\u0632', color: '#22c55e', icon: Trophy },
  { id: 'lost', label: 'Lost', labelAr: '\u062e\u0633\u0627\u0631\u0629', color: '#ef4444', icon: XCircle },
];

// Required tasks per stage
const REQUIRED_TASKS = {
  new: { en: 'Contact the Lead', ar: '\u0627\u0644\u062a\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u0639\u0645\u064a\u0644 \u0627\u0644\u0645\u062d\u062a\u0645\u0644' },
  contacted: { en: 'Obtain Registration/Prequalification', ar: '\u0627\u0644\u062d\u0635\u0648\u0644 \u0639\u0644\u0649 \u0627\u0644\u062a\u0633\u062c\u064a\u0644/\u0627\u0644\u062a\u0623\u0647\u064a\u0644 \u0627\u0644\u0645\u0633\u0628\u0642' },
  qualified: { en: 'Request Invitation to Bid', ar: '\u0637\u0644\u0628 \u062f\u0639\u0648\u0629 \u0644\u0644\u0645\u0646\u0627\u0642\u0635\u0629' },
  opportunity: { en: 'Submit Proposal', ar: '\u062a\u0642\u062f\u064a\u0645 \u0627\u0644\u0639\u0631\u0636' },
  submitted: { en: 'Follow-up with Client', ar: '\u0645\u062a\u0627\u0628\u0639\u0629 \u0645\u0639 \u0627\u0644\u0639\u0645\u064a\u0644' },
  won: null,
  lost: null,
};

// Stage transition rules - defines which stages can transition to which
// Leads can move forward to next stage OR backward to previous stage
const STAGE_TRANSITIONS = {
  new: { forward: ['contacted'], backward: [] },
  contacted: { forward: ['qualified'], backward: ['new'] },
  qualified: { forward: ['opportunity'], backward: ['contacted'] },
  opportunity: { forward: ['submitted'], backward: ['qualified'] },
  submitted: { forward: ['won', 'lost'], backward: ['opportunity'] },
  won: { forward: [], backward: ['submitted'] },
  lost: { forward: [], backward: ['submitted'] },
};

// Mandatory fields per stage - must be filled before moving to next stage
const MANDATORY_FIELDS = {
  new: {
    fields: ['contactPersons'],  // At least one contact with name, email, phone
    labels: {
      en: ['At least one contact person with Name, Email, and Phone'],
      ar: ['جهة اتصال واحدة على الأقل مع الاسم والبريد الإلكتروني والهاتف']
    },
    customValidation: (lead) => {
      const contacts = lead.contactPersons || [];
      if (contacts.length === 0) return false;
      // Check if at least one contact has all required fields
      return contacts.some(c => c.name && c.email && c.phone);
    }
  },
  contacted: {
    fields: ['registrationCompleted', 'prequalDeadline'],
    labels: {
      en: ['Registration Completed', 'Prequalification Deadline'],
      ar: ['تم التسجيل', 'موعد التأهيل المسبق']
    }
  },
  qualified: {
    fields: ['bidInviteReceived', 'bidInviteDate'],
    labels: {
      en: ['Bid Invitation Received', 'Bid Invitation Date'],
      ar: ['تم استلام دعوة المناقصة', 'تاريخ دعوة المناقصة']
    }
  },
  opportunity: {
    fields: ['submissionDeadline', 'estimatedValue'],
    labels: {
      en: ['Submission Deadline', 'Estimated Value'],
      ar: ['موعد التقديم', 'القيمة المقدرة']
    }
  },
  submitted: {
    fields: ['submittedAt', 'proposalValue', 'expectedDecisionDate'],
    labels: {
      en: ['Submission Date', 'Proposal Value', 'Expected Decision Date'],
      ar: ['تاريخ التقديم', 'قيمة العرض', 'تاريخ القرار المتوقع']
    }
  },
  won: {
    fields: ['wonAt', 'contractValue'],
    labels: {
      en: ['Won Date', 'Contract Value'],
      ar: ['تاريخ الفوز', 'قيمة العقد']
    }
  },
  lost: {
    fields: ['lostAt', 'lostReason'],
    labels: {
      en: ['Lost Date', 'Lost Reason'],
      ar: ['تاريخ الخسارة', 'سبب الخسارة']
    }
  }
};

// Helper to get nested object value
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

// Helper to validate mandatory fields for a stage
const validateStageFields = (lead, stage) => {
  const config = MANDATORY_FIELDS[stage];
  if (!config) return { valid: true, missingFields: [] };
  
  // Use custom validation if provided
  if (config.customValidation) {
    const isValid = config.customValidation(lead);
    if (!isValid) {
      return { valid: false, missingFields: [{ field: config.fields[0], label: config.labels }] };
    }
    return { valid: true, missingFields: [] };
  }
  
  const missingFields = [];
  config.fields.forEach((field, index) => {
    const value = getNestedValue(lead, field);
    // For boolean fields like registrationCompleted, check if explicitly true
    // For other fields, check if truthy (not empty/null/undefined)
    const isBooleanField = ['registrationCompleted', 'bidInviteReceived'].includes(field);
    if (isBooleanField) {
      if (value !== true) {
        missingFields.push({ field, label: config.labels });
      }
    } else if (!value || (typeof value === 'string' && value.trim() === '')) {
      missingFields.push({ field, label: config.labels });
    }
  });
  
  return { valid: missingFields.length === 0, missingFields };
};

// Helper to get allowed forward stages (next stages)
const getAllowedForwardStages = (currentStage) => {
  return STAGE_TRANSITIONS[currentStage]?.forward || [];
};

// Helper to get allowed backward stages (previous stages)
const getAllowedBackwardStages = (currentStage) => {
  return STAGE_TRANSITIONS[currentStage]?.backward || [];
};

// Helper to get deadline status
const getDeadlineStatus = (deadline, language) => {
  if (!deadline) return { color: 'text-gray-400', bg: 'bg-gray-100', label: language === 'ar' ? '\u0628\u062f\u0648\u0646 \u0645\u0648\u0639\u062f' : 'No deadline' };
  const date = new Date(deadline);
  if (isPast(date) && !isToday(date)) {
    return { color: 'text-red-600', bg: 'bg-red-50', label: language === 'ar' ? '\u0645\u062a\u0623\u062e\u0631' : 'Overdue' };
  }
  if (isToday(date)) {
    return { color: 'text-orange-600', bg: 'bg-orange-50', label: language === 'ar' ? '\u0627\u0644\u064a\u0648\u0645' : 'Due today' };
  }
  const daysLeft = differenceInDays(date, new Date());
  if (daysLeft <= 3) {
    return { color: 'text-amber-600', bg: 'bg-amber-50', label: language === 'ar' ? `${daysLeft} \u0623\u064a\u0627\u0645` : `${daysLeft} days left` };
  }
  return { color: 'text-green-600', bg: 'bg-green-50', label: language === 'ar' ? `${daysLeft} \u064a\u0648\u0645` : `${daysLeft} days left` };
};

// Multi-select filter component
const MultiSelectFilter = ({ label, icon: Icon, options, selected, onChange, language, testId }) => {
  const [open, setOpen] = useState(false);
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-10 px-3 justify-between min-w-[130px]" data-testid={testId}>
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[80px]">
              {selected.length === 0 ? label : `${selected.length} ${language === 'ar' ? '\u0645\u062d\u062f\u062f' : 'selected'}`}
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <ScrollArea className="h-[200px]">
          <div className="p-2 space-y-1">
            {options.map((option) => (
              <div
                key={option.value}
                className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-muted ${selected.includes(option.value) ? 'bg-primary/10' : ''}`}
                onClick={() => toggleOption(option.value)}
              >
                <Checkbox checked={selected.includes(option.value)} className="pointer-events-none" />
                {option.icon && <span>{option.icon}</span>}
                <span className="text-sm">{option.label}</span>
              </div>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default function OpportunitiesPage() {
  const { language, isRTL } = useLanguage();
  const { getToken, user } = useAuth();
  const planGate = usePlanGate();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('pipeline');
  const [activeStage, setActiveStage] = useState('new');
  const [isExporting, setIsExporting] = useState(false);
  
  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  // WO-33 — helpful empty screen when the CRM is empty (onboarding switch on).
  const onb = useOnboarding();
  const crmFiltered = !!searchInput || selectedCountries.length > 0 || selectedTypes.length > 0 || selectedStatuses.length > 0;
  const clearCrmFilters = () => { setSearchInput(''); setSelectedCountries([]); setSelectedTypes([]); setSelectedStatuses([]); };
  
  // Dialogs
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  
  // Edit form
  const [editForm, setEditForm] = useState({});
  
  // Check if user can export (not free plan)
  const canExport = user?.plan_id?.toLowerCase() !== 'free';

  // Admin-only conversion (Opportunity → Project / Company)
  const isAdmin = user?.role === 'admin';
  const CONVERTIBLE_STATUSES = ['contacted', 'qualified', 'opportunity', 'submitted', 'won'];
  const [converting, setConverting] = useState(null); // 'project' | 'company' | null

  const handleConvertOpportunity = async (target) => {
    if (!selectedLead?.id) return;
    if (!CONVERTIBLE_STATUSES.includes(editForm.status)) {
      toast.error(language === 'ar'
        ? 'يجب أن تكون الفرصة في مرحلة "تم التواصل" على الأقل'
        : 'Opportunity must be at least at "Contacted" stage');
      return;
    }
    const endpoint = target === 'project' ? 'convert-to-project' : 'convert-to-company';
    setConverting(target);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/opportunities/${selectedLead.id}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || 'Conversion failed');
      }
      const newId = target === 'project' ? data.project_id : data.company_id;
      const alreadyConverted = !!data.already_converted;
      // Update local form state so the UI reflects converted status immediately
      setEditForm(prev => ({
        ...prev,
        ...(target === 'project'
          ? { convertedToProjectId: newId, convertedToProjectAt: new Date().toISOString() }
          : { convertedToCompanyId: newId, convertedToCompanyAt: new Date().toISOString() }),
      }));
      toast.success(
        alreadyConverted
          ? (language === 'ar' ? 'تم التحويل بالفعل' : 'Already converted')
          : target === 'project'
            ? (language === 'ar' ? 'تم تحويل الفرصة إلى مشروع' : 'Converted to Project')
            : (language === 'ar' ? 'تم تحويل الفرصة إلى شركة' : 'Converted to Company')
      );
      // Refresh list to keep counts/state consistent
      fetchLeads();
    } catch (err) {
      console.error('Convert error:', err);
      toast.error(err.message || (language === 'ar' ? 'فشل التحويل' : 'Conversion failed'));
    } finally {
      setConverting(null);
    }
  };

  // Export handler
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/opportunities/export?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) return;
        let detail = '';
        try { detail = (await response.clone().json()).detail; } catch { /* non-json */ }
        throw new Error(detail || (language === 'ar' ? 'تعذّر التصدير' : 'Export could not be completed'));
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `opportunities_export.${format === 'excel' ? 'xlsx' : format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(language === 'ar' 
        ? `تم تصدير الفرص بنجاح (${format.toUpperCase()})`
        : `Opportunities exported successfully (${format.toUpperCase()})`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || (language === 'ar' ? 'فشل التصدير' : 'Export failed'));
    } finally {
      setIsExporting(false);
    }
  };
  
  // Stats
  const [stats, setStats] = useState({
    total: 0, new: 0, contacted: 0, qualified: 0, opportunity: 0, 
    submitted: 0, won: 0, lost: 0, totalValue: 0, wonValue: 0
  });
  
  // New lead form
  const [newLeadForm, setNewLeadForm] = useState({
    name: '',
    type: 'project',
    country: 'SA',
    city: '',
    website: '',
    priority: 2,
    probability: 25,
    industries: [],
    notes: '',
    contact_person: { name: '', position: '', email: '', phone: '' }
  });

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const params = new URLSearchParams();
      
      if (searchInput) params.append('search', searchInput);
      if (selectedCountries.length > 0) params.append('country', selectedCountries[0]);
      if (selectedTypes.length > 0) params.append('type', selectedTypes[0]);
      if (selectedStatuses.length > 0) params.append('status', selectedStatuses[0]);
      params.append('page_size', '100');
      
      const response = await fetch(`${API_URL}/api/opportunities?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          toast.error(language === 'ar' ? '\u0627\u0646\u062a\u0647\u062a \u0627\u0644\u062c\u0644\u0633\u0629' : 'Session expired');
          return;
        }
        throw new Error('Failed to fetch opportunities');
      }
      
      const data = await response.json();
      
      // Map API response to frontend format
      const mappedLeads = (data.items || []).map(item => ({
        id: item._id || item.id,
        ref_id: item.ref_id,
        name: item.name,
        type: item.type,
        status: item.status,
        source: item.source,
        sourceId: item.source_id,
        country: item.country,
        city: item.city,
        website: item.website,
        priority: item.priority || 2,
        probability: item.probability || 25,
        industries: item.industries || [],
        estimatedValue: item.estimated_value,
        awardedAmount: item.awarded_amount,
        contactPerson: item.contact_person,  // Legacy single contact
        contactPersons: item.contact_persons || (item.contact_person ? [item.contact_person] : []),  // Multiple contacts
        assignedTo: item.assigned_to,
        assignedToName: item.assigned_to_name,
        taskDeadline: item.task_deadline,
        taskCompleted: item.task_completed || false,
        notes: item.notes,
        lostReason: item.lost_reason,
        lostToCompetitor: item.lost_to_competitor,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        // Stage-specific fields
        registrationCompleted: item.registration_completed || false,
        prequalDeadline: item.prequal_deadline,
        bidInviteReceived: item.bid_invite_received || false,
        bidInviteDate: item.bid_invite_date,
        submissionDeadline: item.submission_deadline,
        submittedAt: item.submitted_at,
        expectedDecisionDate: item.expected_decision_date,
        proposalValue: item.proposal_value,
        wonAt: item.won_at,
        contractValue: item.contract_value,
        lostAt: item.lost_at,
        // Admin conversion tracking
        convertedToProjectId: item.converted_to_project_id,
        convertedToCompanyId: item.converted_to_company_id,
        convertedToProjectAt: item.converted_to_project_at,
        convertedToCompanyAt: item.converted_to_company_at,
        userId: item.user_id,
        // WO-21 — who created it and whether this person may delete it. Both
        // answered by the server: the rule now includes "the Team Admin may
        // delete a Team member's entry", which the page cannot work out alone.
        ownerName: item.owner_name || null,
        isOwn: item.is_own === true,
        canDelete: item.can_delete === true,
        isMasked: item._is_masked === true
      }));
      
      setLeads(mappedLeads);
      
      // Calculate stats from loaded data
      const newStats = { total: mappedLeads.length };
      STAGES.forEach(s => { newStats[s.id] = mappedLeads.filter(l => l.status === s.id).length; });
      newStats.totalValue = mappedLeads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0);
      newStats.wonValue = mappedLeads.filter(l => l.status === 'won').reduce((sum, l) => sum + (l.awardedAmount || l.estimatedValue || 0), 0);
      setStats(newStats);
      
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error(language === 'ar' ? '\u0641\u0634\u0644 \u0641\u064a \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getToken, language, searchInput, selectedCountries, selectedTypes, selectedStatuses]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const getLeadsByStatus = (status) => leads.filter(l => l.status === status);

  const openLeadDetails = (lead) => {
    setSelectedLead(lead);
    // Initialize contactPersons - migrate from legacy single contact if needed
    const contactPersons = lead.contactPersons?.length > 0 
      ? [...lead.contactPersons]
      : (lead.contactPerson ? [{ ...lead.contactPerson }] : [{ name: '', email: '', phone: '', position: '' }]);
    setEditForm({
      ...lead,
      contactPerson: lead.contactPerson ? { ...lead.contactPerson } : {},
      contactPersons: contactPersons
    });
    setShowLeadDialog(true);
  };

  // Maps the edit form to the API payload. Shared by Save and stage-change so
  // a stage move persists ALL current field values, not just `status`.
  const buildLeadUpdateData = (form, statusOverride) => {
    const validContacts = (form.contactPersons || []).filter(c =>
      c.name || c.email || c.phone || c.position
    );
    return {
      name: form.name,
      type: form.type,
      status: statusOverride != null ? statusOverride : form.status,
      country: form.country,
      city: form.city,
      website: form.website,
      priority: form.priority,
      probability: form.probability,
      industries: form.industries,
      estimated_value: form.estimatedValue,
      awarded_amount: form.awardedAmount,
      notes: form.notes,
      contact_person: validContacts[0] || form.contactPerson,
      contact_persons: validContacts,
      task_deadline: form.taskDeadline,
      task_completed: form.taskCompleted,
      lost_reason: form.lostReason,
      lost_to_competitor: form.lostToCompetitor,
      assigned_to: form.assignedTo,
      assigned_to_name: form.assignedToName,
      registration_completed: form.registrationCompleted,
      prequal_deadline: form.prequalDeadline,
      bid_invite_received: form.bidInviteReceived,
      bid_invite_date: form.bidInviteDate,
      submission_deadline: form.submissionDeadline,
      submitted_at: form.submittedAt,
      expected_decision_date: form.expectedDecisionDate,
      proposal_value: form.proposalValue,
      won_at: form.wonAt,
      contract_value: form.contractValue,
      lost_at: form.lostAt,
    };
  };

  // Persist a stage change to the backend with the FULL current form (avoids
  // losing fields the user just filled in for the current stage).
  const persistStageChange = async (newStatus) => {
    const stageToken = getToken();
    return fetch(`${API_URL}/api/opportunities/${selectedLead.id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${stageToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(buildLeadUpdateData(editForm, newStatus)),
    });
  };

  const handleSaveLead = async () => {
    setSavingLead(true);
    try {
      const token = getToken();
      
      // Filter out empty contacts (all fields empty)
      const validContacts = (editForm.contactPersons || []).filter(c => 
        c.name || c.email || c.phone || c.position
      );
      
      // Map frontend form to API format, including all stage-specific fields
      const updateData = {
        name: editForm.name,
        type: editForm.type,
        status: editForm.status,
        country: editForm.country,
        city: editForm.city,
        website: editForm.website,
        priority: editForm.priority,
        probability: editForm.probability,
        industries: editForm.industries,
        estimated_value: editForm.estimatedValue,
        awarded_amount: editForm.awardedAmount,
        notes: editForm.notes,
        contact_person: validContacts[0] || editForm.contactPerson,  // Legacy: first contact
        contact_persons: validContacts,  // All contacts
        task_deadline: editForm.taskDeadline,
        task_completed: editForm.taskCompleted,
        lost_reason: editForm.lostReason,
        lost_to_competitor: editForm.lostToCompetitor,
        assigned_to: editForm.assignedTo,
        assigned_to_name: editForm.assignedToName,
        // Stage-specific fields
        registration_completed: editForm.registrationCompleted,
        prequal_deadline: editForm.prequalDeadline,
        bid_invite_received: editForm.bidInviteReceived,
        bid_invite_date: editForm.bidInviteDate,
        submission_deadline: editForm.submissionDeadline,
        submitted_at: editForm.submittedAt,
        expected_decision_date: editForm.expectedDecisionDate,
        proposal_value: editForm.proposalValue,
        won_at: editForm.wonAt,
        contract_value: editForm.contractValue,
        lost_at: editForm.lostAt
      };
      
      const response = await fetch(`${API_URL}/api/opportunities/${selectedLead.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) { setSavingLead(false); return; }
        throw new Error('Failed to update opportunity');
      }
      
      // Sync contacts to Contacts section (validContacts already defined above)
      if (validContacts.length > 0) {
        try {
          await fetch(`${API_URL}/api/contacts/sync-from-opportunity`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              opportunity_id: selectedLead.id,
              opportunity_name: editForm.name,
              contacts: validContacts,
              country: editForm.country,
              industries: editForm.industries || []
            })
          });
          console.log('[SYNC] Contacts synced to Contacts section');
        } catch (syncError) {
          console.error('[SYNC] Error syncing contacts:', syncError);
          // Don't fail the save if sync fails
        }
      }
      
      toast.success(language === 'ar' ? '\u062a\u0645 \u062d\u0641\u0638 \u0627\u0644\u0639\u0645\u064a\u0644 \u0627\u0644\u0645\u062d\u062a\u0645\u0644' : 'Lead saved successfully');
      setShowLeadDialog(false);
      fetchLeads();
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error(language === 'ar' ? '\u0641\u0634\u0644 \u0641\u064a \u062d\u0641\u0638 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a' : 'Failed to save');
    } finally {
      setSavingLead(false);
    }
  };

  const handleCreateLead = async () => {
    if (!newLeadForm.name.trim()) {
      toast.error(language === 'ar' ? '\u0627\u0633\u0645 \u0627\u0644\u0639\u0645\u064a\u0644 \u0627\u0644\u0645\u062d\u062a\u0645\u0644 \u0645\u0637\u0644\u0648\u0628' : 'Lead name is required');
      return;
    }
    
    setSavingLead(true);
    try {
      const token = getToken();
      
      const createData = {
        name: newLeadForm.name.trim(),
        type: newLeadForm.type,
        country: newLeadForm.country,
        city: newLeadForm.city,
        website: newLeadForm.website,
        priority: newLeadForm.priority,
        probability: newLeadForm.probability,
        industries: newLeadForm.industries,
        notes: newLeadForm.notes,
        contact_person: newLeadForm.contact_person,
        source: 'manual'
      };
      
      const response = await fetch(`${API_URL}/api/opportunities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createData)
      });
      
      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) { setSavingLead(false); return; }
        throw new Error('Failed to create opportunity');
      }
      
      toast.success(language === 'ar' ? '\u062a\u0645 \u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u0639\u0645\u064a\u0644 \u0627\u0644\u0645\u062d\u062a\u0645\u0644' : 'Lead created successfully');
      setShowAddDialog(false);
      setNewLeadForm({
        name: '',
        type: 'project',
        country: 'SA',
        city: '',
        website: '',
        priority: 2,
        probability: 25,
        industries: [],
        notes: '',
        contact_person: { name: '', position: '', email: '', phone: '' }
      });
      fetchLeads();
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error(language === 'ar' ? '\u0641\u0634\u0644 \u0641\u064a \u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u0639\u0645\u064a\u0644' : 'Failed to create lead');
    } finally {
      setSavingLead(false);
    }
  };

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Alert dialog state for opportunities
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [alertTarget, setAlertTarget] = useState(null);
  const [alertName, setAlertName] = useState('');
  const [alertFrequency, setAlertFrequency] = useState('realtime');
  const [alertChannels, setAlertChannels] = useState(['in_app', 'email']);
  const [savingAlert, setSavingAlert] = useState(false);

  const handleDeleteClick = (leadId) => {
    console.log('[DELETE] Delete button clicked, leadId:', leadId);
    if (!leadId) {
      console.error('[DELETE] No lead ID provided');
      toast.error(language === 'ar' ? 'خطأ: لم يتم تحديد العميل' : 'Error: No lead selected');
      return;
    }
    setDeletingLeadId(leadId);
    setDeleteConfirmOpen(true);
  };

  // Create alert for opportunity
  const handleCreateOpportunityAlert = async () => {
    if (!alertTarget || !alertName.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال اسم التنبيه' : 'Please enter an alert name');
      return;
    }
    
    setSavingAlert(true);
    try {
      const alertData = {
        name: alertName,
        filters: {
          entity_type: 'opportunity',
          entity_id: alertTarget.id,
          entity_title: alertTarget.title,
          countries: [],
          industries: [],
          keywords: [],
          min_value: null,
          max_value: null,
          sources: []
        },
        frequency: alertFrequency,
        channels: alertChannels,
        is_active: true
      };
      
      const response = await fetch(`${API_URL}/api/alerts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(alertData)
      });
      
      if (response.status === 403 || response.status === 402) {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) { setSavingAlert(false); return; }
        toast.error(language === 'ar' 
          ? 'التنبيهات غير متاحة في خطتك الحالية'
          : 'Alerts not available on your plan');
        setSavingAlert(false);
        return;
      }
      
      if (!response.ok) throw new Error('Failed to create alert');
      
      toast.success(language === 'ar' 
        ? 'تم إنشاء التنبيه! ستتلقى إشعارات عند أي تحديث.'
        : 'Alert created! You\'ll be notified of any updates.');
      
      setShowAlertDialog(false);
      setAlertTarget(null);
      setAlertName('');
    } catch (error) {
      console.error('Error creating alert:', error);
      toast.error(language === 'ar' ? 'فشل إنشاء التنبيه' : 'Failed to create alert');
    } finally {
      setSavingAlert(false);
    }
  };

  // Open alert dialog with default name
  useEffect(() => {
    if (showAlertDialog && alertTarget) {
      setAlertName(`${language === 'ar' ? 'فرصة' : 'Opportunity'}: ${alertTarget.title?.substring(0, 50) || 'Unknown'}`);
    }
  }, [showAlertDialog, alertTarget, language]);

  const handleConfirmDelete = async () => {
    console.log('[DELETE] Confirming delete for leadId:', deletingLeadId);
    if (!deletingLeadId) {
      console.error('[DELETE] No deletingLeadId set');
      return;
    }
    
    setIsDeleting(true);
    try {
      const token = getToken();
      console.log('[DELETE] Making DELETE request to:', `${API_URL}/api/opportunities/${deletingLeadId}`);
      
      const response = await fetch(`${API_URL}/api/opportunities/${deletingLeadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[DELETE] Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[DELETE] Error response:', errorData);
        throw new Error(errorData.detail || 'Failed to delete opportunity');
      }
      
      const result = await response.json();
      console.log('[DELETE] Success result:', result);
      
      toast.success(language === 'ar' ? 'تم حذف العميل المحتمل' : 'Lead deleted successfully');
      setDeleteConfirmOpen(false);
      setShowLeadDialog(false);
      setDeletingLeadId(null);
      fetchLeads();
    } catch (error) {
      console.error('[DELETE] Error deleting lead:', error);
      toast.error(language === 'ar' ? 'فشل في حذف العميل' : `Failed to delete lead: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    console.log('[DELETE] Delete cancelled');
    setDeleteConfirmOpen(false);
    setDeletingLeadId(null);
  };

  const translations = {
    en: {
      title: 'Opportunities',
      subtitle: 'Manage your sales pipeline and track leads',
      searchPlaceholder: 'Search leads...',
      allCountries: 'Countries',
      allTypes: 'Type',
      allStatuses: 'Status',
      company: 'Company',
      project: 'Project',
      addLead: 'Add Lead',
      exportCSV: 'Export CSV',
      pipeline: 'Pipeline',
      leadsList: 'Leads',
      statistics: 'Stats',
      totalLeads: 'Total Leads',
      wonValue: 'Won Value',
      pipelineValue: 'Pipeline Value',
      conversionRate: 'Conversion Rate',
      noLeads: 'No leads in this stage',
      viewDetails: 'View Details',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      leadDetails: 'Lead Details',
      createLead: 'Create New Lead',
      basicInfo: 'Basic Information',
      contactInfo: 'Contact Person',
      leadName: 'Lead Name',
      leadType: 'Lead Type',
      country: 'Country',
      city: 'City',
      website: 'Website',
      priority: 'Priority',
      probability: 'Probability',
      industries: 'Industries',
      notes: 'Notes',
      contactName: 'Contact Name',
      position: 'Position',
      email: 'Email',
      phone: 'Phone',
      estimatedValue: 'Estimated Value',
      taskDeadline: 'Task Deadline',
      source: 'Source',
      status: 'Status',
      assignedTo: 'Assigned To',
      created: 'Created',
      updated: 'Updated'
    },
    ar: {
      title: '\u0627\u0644\u0641\u0631\u0635',
      subtitle: '\u0625\u062f\u0627\u0631\u0629 \u062e\u0637 \u0623\u0646\u0627\u0628\u064a\u0628 \u0627\u0644\u0645\u0628\u064a\u0639\u0627\u062a \u0648\u062a\u062a\u0628\u0639 \u0627\u0644\u0639\u0645\u0644\u0627\u0621 \u0627\u0644\u0645\u062d\u062a\u0645\u0644\u064a\u0646',
      searchPlaceholder: '\u0628\u062d\u062b \u0641\u064a \u0627\u0644\u0639\u0645\u0644\u0627\u0621 \u0627\u0644\u0645\u062d\u062a\u0645\u0644\u064a\u0646...',
      allCountries: '\u0627\u0644\u062f\u0648\u0644',
      allTypes: '\u0627\u0644\u0646\u0648\u0639',
      allStatuses: '\u0627\u0644\u062d\u0627\u0644\u0629',
      company: '\u0634\u0631\u0643\u0629',
      project: '\u0645\u0634\u0631\u0648\u0639',
      addLead: '\u0625\u0636\u0627\u0641\u0629 \u0639\u0645\u064a\u0644',
      exportCSV: '\u062a\u0635\u062f\u064a\u0631 CSV',
      pipeline: '\u062e\u0637 \u0627\u0644\u0623\u0646\u0627\u0628\u064a\u0628',
      leadsList: '\u0627\u0644\u0639\u0645\u0644\u0627\u0621',
      statistics: '\u0627\u0644\u0625\u062d\u0635\u0627\u0626\u064a\u0627\u062a',
      totalLeads: '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0639\u0645\u0644\u0627\u0621',
      wonValue: '\u0642\u064a\u0645\u0629 \u0627\u0644\u0641\u0648\u0632',
      pipelineValue: '\u0642\u064a\u0645\u0629 \u062e\u0637 \u0627\u0644\u0623\u0646\u0627\u0628\u064a\u0628',
      conversionRate: '\u0645\u0639\u062f\u0644 \u0627\u0644\u062a\u062d\u0648\u064a\u0644',
      noLeads: '\u0644\u0627 \u064a\u0648\u062c\u062f \u0639\u0645\u0644\u0627\u0621 \u0641\u064a \u0647\u0630\u0647 \u0627\u0644\u0645\u0631\u062d\u0644\u0629',
      viewDetails: '\u0639\u0631\u0636 \u0627\u0644\u062a\u0641\u0627\u0635\u064a\u0644',
      save: '\u062d\u0641\u0638',
      cancel: '\u0625\u0644\u063a\u0627\u0621',
      delete: '\u062d\u0630\u0641',
      leadDetails: '\u062a\u0641\u0627\u0635\u064a\u0644 \u0627\u0644\u0639\u0645\u064a\u0644',
      createLead: '\u0625\u0646\u0634\u0627\u0621 \u0639\u0645\u064a\u0644 \u062c\u062f\u064a\u062f',
      basicInfo: '\u0645\u0639\u0644\u0648\u0645\u0627\u062a \u0623\u0633\u0627\u0633\u064a\u0629',
      contactInfo: '\u062c\u0647\u0629 \u0627\u0644\u0627\u062a\u0635\u0627\u0644',
      leadName: '\u0627\u0633\u0645 \u0627\u0644\u0639\u0645\u064a\u0644',
      leadType: '\u0646\u0648\u0639 \u0627\u0644\u0639\u0645\u064a\u0644',
      country: '\u0627\u0644\u062f\u0648\u0644\u0629',
      city: '\u0627\u0644\u0645\u062f\u064a\u0646\u0629',
      website: '\u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a',
      priority: '\u0627\u0644\u0623\u0648\u0644\u0648\u064a\u0629',
      probability: '\u0627\u0644\u0627\u062d\u062a\u0645\u0627\u0644\u064a\u0629',
      industries: '\u0627\u0644\u0642\u0637\u0627\u0639\u0627\u062a',
      notes: '\u0645\u0644\u0627\u062d\u0638\u0627\u062a',
      contactName: '\u0627\u0633\u0645 \u062c\u0647\u0629 \u0627\u0644\u0627\u062a\u0635\u0627\u0644',
      position: '\u0627\u0644\u0645\u0646\u0635\u0628',
      email: '\u0627\u0644\u0628\u0631\u064a\u062f \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a',
      phone: '\u0627\u0644\u0647\u0627\u062a\u0641',
      estimatedValue: '\u0627\u0644\u0642\u064a\u0645\u0629 \u0627\u0644\u0645\u0642\u062f\u0631\u0629',
      taskDeadline: '\u0645\u0648\u0639\u062f \u0627\u0644\u0645\u0647\u0645\u0629',
      source: '\u0627\u0644\u0645\u0635\u062f\u0631',
      status: '\u0627\u0644\u062d\u0627\u0644\u0629',
      assignedTo: '\u0645\u0639\u064a\u0646 \u0644\u0640',
      created: '\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u0625\u0646\u0634\u0627\u0621',
      updated: '\u0622\u062e\u0631 \u062a\u062d\u062f\u064a\u062b'
    }
  };

  const txt = translations[language];

  // Lead Card Component
  const LeadCard = ({ lead }) => {
    const stage = STAGES.find(s => s.id === lead.status);
    const requiredTask = REQUIRED_TASKS[lead.status];
    const deadlineStatus = getDeadlineStatus(lead.taskDeadline, language);
    
    // Stage-specific field renderer
    const renderStageFields = () => {
      switch (lead.status) {
        case 'new':
          return (
            <>
              {/* Source */}
              {lead.source && (
                <div className={`flex items-center gap-1 text-xs text-muted-foreground mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Globe className="w-3 h-3" />
                  <span className="capitalize">{lead.source === 'news' ? (language === 'ar' ? 'أخبار' : 'News') : lead.source === 'tender' ? (language === 'ar' ? 'مناقصة' : 'Tender') : lead.source === 'project' ? (language === 'ar' ? 'مشروع' : 'Project') : lead.source === 'company' ? (language === 'ar' ? 'شركة' : 'Company') : lead.source}</span>
                </div>
              )}
              {/* Contact Person */}
              {lead.contactPerson && (
                <div className={`flex items-center gap-1 text-xs text-muted-foreground mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <User className="w-3 h-3" />
                  <span className="truncate">{typeof lead.contactPerson === 'string' ? lead.contactPerson : lead.contactPerson?.name || ''}</span>
                </div>
              )}
            </>
          );
        
        case 'contacted':
          return (
            <>
              {/* Registration Status */}
              <div className={`flex items-center gap-1 text-xs mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <FileCheck className="w-3 h-3 text-blue-500" />
                <span className={lead.registrationCompleted ? 'text-green-600' : 'text-muted-foreground'}>
                  {lead.registrationCompleted 
                    ? (language === 'ar' ? 'تم التسجيل' : 'Registered') 
                    : (language === 'ar' ? 'في انتظار التسجيل' : 'Pending Registration')}
                </span>
              </div>
              {/* Prequalification Deadline */}
              {lead.prequalDeadline && (
                <div className={`flex items-center gap-1 text-xs text-amber-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Calendar className="w-3 h-3" />
                  <span>{language === 'ar' ? 'موعد التأهيل:' : 'Prequal:'} {format(new Date(lead.prequalDeadline), 'MMM d')}</span>
                </div>
              )}
            </>
          );
        
        case 'qualified':
          return (
            <>
              {/* Bid Invitation Status */}
              <div className={`flex items-center gap-1 text-xs mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <FileText className="w-3 h-3 text-purple-500" />
                <span className={lead.bidInviteReceived ? 'text-green-600' : 'text-muted-foreground'}>
                  {lead.bidInviteReceived 
                    ? (language === 'ar' ? 'تم استلام الدعوة' : 'Invite Received') 
                    : (language === 'ar' ? 'في انتظار الدعوة' : 'Awaiting Invite')}
                </span>
              </div>
              {/* Bid Invitation Date */}
              {lead.bidInviteDate && (
                <div className={`flex items-center gap-1 text-xs text-muted-foreground mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Calendar className="w-3 h-3" />
                  <span className="truncate">{new Date(lead.bidInviteDate).toLocaleDateString()}</span>
                </div>
              )}
            </>
          );
        
        case 'opportunity':
          return (
            <>
              {/* Submission Deadline */}
              {lead.submissionDeadline && (
                <div className={`flex items-center gap-1 text-xs mb-2 ${isRTL ? 'flex-row-reverse' : ''} ${
                  isPast(new Date(lead.submissionDeadline)) ? 'text-red-600' : 'text-amber-600'
                }`}>
                  <Clock className="w-3 h-3" />
                  <span>{language === 'ar' ? 'موعد التقديم:' : 'Submit by:'} {format(new Date(lead.submissionDeadline), 'MMM d')}</span>
                </div>
              )}
              {/* Estimated Value - prominent for opportunity stage */}
              {toMillions(lead.estimatedValue, lead.currency) && (
                <div className={`flex items-center gap-1 text-sm font-semibold text-emerald-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <DollarSign className="w-4 h-4" />
                  {toMillions(lead.estimatedValue, lead.currency)}
                </div>
              )}
            </>
          );
        
        case 'submitted':
          return (
            <>
              {/* Submission Date */}
              {lead.submittedAt && (
                <div className={`flex items-center gap-1 text-xs text-green-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle className="w-3 h-3" />
                  <span>{language === 'ar' ? 'تم التقديم:' : 'Submitted:'} {format(new Date(lead.submittedAt), 'MMM d')}</span>
                </div>
              )}
              {/* Expected Decision Date */}
              {lead.expectedDecisionDate && (
                <div className={`flex items-center gap-1 text-xs text-blue-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Calendar className="w-3 h-3" />
                  <span>{language === 'ar' ? 'القرار المتوقع:' : 'Decision:'} {format(new Date(lead.expectedDecisionDate), 'MMM d')}</span>
                </div>
              )}
              {/* Proposal Value */}
              {toMillions(lead.proposalValue, lead.currency) && (
                <div className={`flex items-center gap-1 text-sm font-semibold text-emerald-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <DollarSign className="w-4 h-4" />
                  {toMillions(lead.proposalValue, lead.currency)}
                </div>
              )}
            </>
          );
        
        case 'won':
          return (
            <>
              {/* Won Date */}
              {lead.wonAt && (
                <div className={`flex items-center gap-1 text-xs text-green-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Trophy className="w-3 h-3" />
                  <span>{language === 'ar' ? 'تاريخ الفوز:' : 'Won:'} {format(new Date(lead.wonAt), 'MMM d, yyyy')}</span>
                </div>
              )}
              {/* Final Contract Value */}
              {toMillions(lead.contractValue || lead.estimatedValue, lead.currency) && (
                <div className={`flex items-center gap-1 text-sm font-bold text-emerald-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <DollarSign className="w-4 h-4" />
                  {toMillions(lead.contractValue || lead.estimatedValue, lead.currency)}
                  {lead.contractValue && <Badge className="ml-1 text-xs bg-green-100 text-green-700">{language === 'ar' ? 'عقد' : 'Contract'}</Badge>}
                </div>
              )}
            </>
          );
        
        case 'lost':
          return (
            <>
              {/* Lost Date */}
              {lead.lostAt && (
                <div className={`flex items-center gap-1 text-xs text-red-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <XCircle className="w-3 h-3" />
                  <span>{language === 'ar' ? 'تاريخ الخسارة:' : 'Lost:'} {format(new Date(lead.lostAt), 'MMM d, yyyy')}</span>
                </div>
              )}
              {/* Lost Reason */}
              {lead.lostReason && (
                <div className={`text-xs text-muted-foreground mb-2 p-2 bg-red-50 rounded ${isRTL ? 'text-right' : ''}`}>
                  <span className="font-medium">{language === 'ar' ? 'السبب:' : 'Reason:'}</span> {lead.lostReason}
                </div>
              )}
              {/* Lost Value */}
              {toMillions(lead.estimatedValue, lead.currency) && (
                <div className={`flex items-center gap-1 text-sm text-gray-400 line-through mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <DollarSign className="w-4 h-4" />
                  {toMillions(lead.estimatedValue, lead.currency)}
                </div>
              )}
            </>
          );
        
        default:
          return null;
      }
    };
    
    return (
      <Card 
        className="cursor-pointer hover:shadow-md transition-all group"
        onClick={() => openLeadDetails(lead)}
        data-testid={`lead-card-${lead.id}`}
      >
        <CardContent className="p-3">
          {/* Ref ID */}
          {lead.ref_id && (
            <div className="mb-2">
              <Badge className="bg-primary text-primary-foreground font-mono text-xs" data-testid={`opportunity-ref-id-${lead.id}`}>
                {lead.ref_id}
              </Badge>
            </div>
          )}
          
          {/* Header */}
          <div className={`flex items-start gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {lead.type === 'company' ? (
              <Building2 className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            ) : (
              <Briefcase className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
            )}
            <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
              <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">{lead.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{txt[lead.type]}</p>
            </div>
          </div>

          {/* Task - only show for stages with tasks */}
          {requiredTask && (
            <div className={`rounded p-2 mb-2 ${lead.taskCompleted ? 'bg-green-50' : deadlineStatus.bg}`}>
              <div className={`flex items-start gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {lead.taskCompleted ? (
                  <CheckCircle className="w-3 h-3 mt-0.5 text-green-600 shrink-0" />
                ) : (
                  <AlertCircle className={`w-3 h-3 mt-0.5 shrink-0 ${deadlineStatus.color}`} />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-xs ${lead.taskCompleted ? 'text-green-700 line-through' : 'text-gray-700'}`}>
                    {requiredTask[language]}
                  </p>
                  {!lead.taskCompleted && lead.taskDeadline && (
                    <span className={`text-xs ${deadlineStatus.color}`}>
                      {format(new Date(lead.taskDeadline), 'MMM d')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stage-specific fields */}
          {renderStageFields()}

          {/* Priority & Probability - show for active stages only */}
          {!['won', 'lost'].includes(lead.status) && (
            <div className={`flex items-center justify-between mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3].map((star) => (
                  <Star key={star} className={`w-3 h-3 ${star <= lead.priority ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                ))}
              </div>
              <Badge variant="secondary" className="text-xs">{lead.probability}%</Badge>
            </div>
          )}

          {/* Location - always show */}
          <div className={`flex items-center gap-1 text-xs text-muted-foreground mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span>{countryFlags[lead.country]}</span>
            <span className="truncate">{lead.city ? `${lead.city}, ` : ''}{countryNames[language][lead.country]}</span>
          </div>

          {/* Value - show only for stages without stage-specific value display */}
          {toMillions(lead.estimatedValue, lead.currency) && !['opportunity', 'submitted', 'won', 'lost'].includes(lead.status) && (
            <div className={`flex items-center gap-1 text-xs font-medium text-emerald-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <DollarSign className="w-3 h-3" />
              {toMillions(lead.estimatedValue, lead.currency)}
            </div>
          )}

          {/* Footer */}
          <div className={`flex items-center justify-between mt-2 pt-2 border-t border-border/50 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center gap-2">
              {lead.assignedToName && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <User className="w-3 h-3" />
                  <span className="truncate max-w-[80px]">{lead.assignedToName}</span>
                </div>
              )}
              <span className="text-xs text-muted-foreground">
                {lead.createdAt && format(new Date(lead.createdAt), 'MMM d')}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Set Alert Button */}
              {OPPORTUNITY_WATCH_SUPPORTED && user?.plan_id !== 'free' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 px-2 text-amber-500 hover:text-amber-600 hover:bg-amber-500/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAlertTarget({ type: 'opportunity', id: lead.id || lead._id, title: lead.name });
                    setShowAlertDialog(true);
                  }}
                  data-testid={`set-alert-${lead.id}`}
                >
                  <Bell className="w-3.5 h-3.5" />
                </Button>
              )}
              <ShareLink path={`/dashboard/crm/${lead.id || lead._id}`} title={lead.name} compact />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="opportunities-page">
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />
      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 shadow-lg">
              <Target className="w-7 h-7 text-white" />
            </div>
            {txt.title}
          </h1>
          <p className="text-muted-foreground mt-2">{txt.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-wo40="header-actions">
          {/* Export Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                disabled={isExporting}
                data-testid="export-opportunities-btn"
              >
                <Download className="w-4 h-4" />
                {isExporting 
                  ? (language === 'ar' ? 'جاري التصدير...' : 'Exporting...') 
                  : (language === 'ar' ? 'تصدير' : 'Export')}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
                <FileJson className="w-4 h-4 mr-2" />
                JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2" />
                CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel')} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={fetchLeads} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            {language === 'ar' ? '\u062a\u062d\u062f\u064a\u062b' : 'Refresh'}
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="bg-primary" data-testid="add-lead-btn">
            <Plus className="w-4 h-4 mr-2" />
            {txt.addLead}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {STAGES.map((stage) => (
          <Card 
            key={stage.id} 
            className="border-l-4 cursor-pointer hover:shadow-md transition-shadow"
            style={{ borderLeftColor: stage.color }}
            onClick={() => { setActiveStage(stage.id); setViewMode('pipeline'); }}
          >
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{language === 'ar' ? stage.labelAr : stage.label}</p>
              <p className="text-xl font-bold" style={{ color: stage.color }}>{stats[stage.id] || 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters & View Toggle */}
      <Card className="border-0 bg-card/50 backdrop-blur-sm">
        <CardContent className="p-3">
          <div className={`flex flex-col md:flex-row gap-3 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className={`absolute top-2.5 ${isRTL ? 'right-3' : 'left-3'} w-4 h-4 text-muted-foreground`} />
              <Input
                placeholder={txt.searchPlaceholder}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`${isRTL ? 'pr-9' : 'pl-9'} h-10`}
                data-testid="search-input"
              />
            </div>

            {/* Filters */}
            <MultiSelectFilter
              label={txt.allCountries}
              icon={Globe}
              options={Object.keys(countryFlags).map(code => ({ value: code, label: countryNames[language][code], icon: countryFlags[code] }))}
              selected={selectedCountries}
              onChange={setSelectedCountries}
              language={language}
              testId="country-filter"
            />
            
            <MultiSelectFilter
              label={txt.allTypes}
              icon={Building2}
              options={[{ value: 'company', label: txt.company }, { value: 'project', label: txt.project }]}
              selected={selectedTypes}
              onChange={setSelectedTypes}
              language={language}
              testId="type-filter"
            />
            
            <MultiSelectFilter
              label={txt.allStatuses}
              icon={Activity}
              options={STAGES.map(s => ({ value: s.id, label: language === 'ar' ? s.labelAr : s.label }))}
              selected={selectedStatuses}
              onChange={setSelectedStatuses}
              language={language}
              testId="status-filter"
            />

            {/* View Toggle */}
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === 'pipeline' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-10 px-3"
                onClick={() => setViewMode('pipeline')}
                data-testid="view-pipeline-btn"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-10 px-3"
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'stats' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-10 px-3"
                onClick={() => setViewMode('stats')}
                data-testid="view-stats-btn"
              >
                <BarChart3 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          {STAGES.map((stage) => (
            <div key={stage.id} className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ))}
        </div>
      ) : (onb.on && leads.length === 0 && language !== 'ar') ? (
        crmFiltered
          ? <OnboardingEmptyState kind="noResults" onClear={clearCrmFilters} />
          : <OnboardingEmptyState kind="crm" to="/dashboard/tenders" />
      ) : viewMode === 'pipeline' ? (
        <>
          {/* Mobile: Tab view */}
          <div className="md:hidden">
            <Tabs value={activeStage} onValueChange={setActiveStage}>
              <TabsList className="w-full grid grid-cols-4 mb-3">
                {STAGES.slice(0, 4).map((stage) => (
                  <TabsTrigger key={stage.id} value={stage.id} className="text-xs px-1">
                    {language === 'ar' ? stage.labelAr.slice(0, 4) : stage.label.slice(0, 4)}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsList className="w-full grid grid-cols-3 mb-3">
                {STAGES.slice(4).map((stage) => (
                  <TabsTrigger key={stage.id} value={stage.id} className="text-xs px-1">
                    {language === 'ar' ? stage.labelAr : stage.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {STAGES.map((stage) => (
                <TabsContent key={stage.id} value={stage.id}>
                  <div className="rounded-lg p-2 text-white font-medium flex items-center justify-between mb-3" style={{ backgroundColor: stage.color }}>
                    <span className="flex items-center gap-2">
                      <stage.icon className="w-4 h-4" />
                      {language === 'ar' ? stage.labelAr : stage.label}
                    </span>
                    <Badge className="bg-white/20 text-white">{getLeadsByStatus(stage.id).length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {getLeadsByStatus(stage.id).length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground text-sm">{txt.noLeads}</div>
                    ) : (
                      getLeadsByStatus(stage.id).map((lead) => <LeadCard key={lead.id} lead={lead} />)
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Desktop: Kanban */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {STAGES.map((stage) => (
              <div key={stage.id} className="flex flex-col">
                <div className="rounded-lg p-2 text-white font-medium flex items-center justify-between text-sm" style={{ backgroundColor: stage.color }}>
                  <span className="flex items-center gap-1.5">
                    <stage.icon className="w-3.5 h-3.5" />
                    <span className="truncate">{language === 'ar' ? stage.labelAr : stage.label}</span>
                  </span>
                  <Badge className="bg-white/20 text-white text-xs px-1.5">{getLeadsByStatus(stage.id).length}</Badge>
                </div>
                <div className="space-y-2 min-h-[200px] max-h-[calc(100vh-320px)] overflow-y-auto mt-2 pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                  {getLeadsByStatus(stage.id).map((lead) => <LeadCard key={lead.id} lead={lead} />)}
                  {getLeadsByStatus(stage.id).length === 0 && (
                    <div className="text-center py-6 text-muted-foreground text-xs">{txt.noLeads}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : viewMode === 'list' ? (
        <Card className="border-0 bg-card/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className={`px-4 py-3 text-xs font-medium text-muted-foreground uppercase ${isRTL ? 'text-right' : 'text-left'}`}>{txt.leadName}</th>
                  <th className={`px-4 py-3 text-xs font-medium text-muted-foreground uppercase ${isRTL ? 'text-right' : 'text-left'}`}>{txt.leadType}</th>
                  <th className={`px-4 py-3 text-xs font-medium text-muted-foreground uppercase ${isRTL ? 'text-right' : 'text-left'}`}>{txt.status}</th>
                  <th className={`px-4 py-3 text-xs font-medium text-muted-foreground uppercase ${isRTL ? 'text-right' : 'text-left'}`}>{txt.country}</th>
                  <th className={`px-4 py-3 text-xs font-medium text-muted-foreground uppercase ${isRTL ? 'text-right' : 'text-left'}`}>{txt.priority}</th>
                  <th className={`px-4 py-3 text-xs font-medium text-muted-foreground uppercase ${isRTL ? 'text-right' : 'text-left'}`}>{txt.probability}</th>
                  <th className={`px-4 py-3 text-xs font-medium text-muted-foreground uppercase ${isRTL ? 'text-right' : 'text-left'}`}>{txt.estimatedValue}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {leads.map((lead) => {
                  const stage = STAGES.find(s => s.id === lead.status);
                  return (
                    <tr key={lead.id} className="hover:bg-muted/50 cursor-pointer" onClick={() => openLeadDetails(lead)}>
                      <td className={`px-4 py-3 ${isRTL ? 'text-right' : ''}`}>
                        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {lead.type === 'company' ? <Building2 className="w-4 h-4 text-blue-500" /> : <Briefcase className="w-4 h-4 text-purple-500" />}
                          <span className="font-medium text-sm">{lead.name}</span>
                        </div>
                      </td>
                      <td className={`px-4 py-3 ${isRTL ? 'text-right' : ''}`}>
                        <Badge variant="outline" className="text-xs capitalize">{txt[lead.type]}</Badge>
                      </td>
                      <td className={`px-4 py-3 ${isRTL ? 'text-right' : ''}`}>
                        <Badge className="text-xs text-white" style={{ backgroundColor: stage?.color }}>{language === 'ar' ? stage?.labelAr : stage?.label}</Badge>
                      </td>
                      <td className={`px-4 py-3 ${isRTL ? 'text-right' : ''}`}>
                        <span className="flex items-center gap-1 text-sm">{countryFlags[lead.country]} {countryNames[language][lead.country]}</span>
                      </td>
                      <td className={`px-4 py-3 ${isRTL ? 'text-right' : ''}`}>
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3].map((star) => (
                            <Star key={star} className={`w-3 h-3 ${star <= lead.priority ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                      </td>
                      <td className={`px-4 py-3 ${isRTL ? 'text-right' : ''}`}>{lead.probability}%</td>
                      <td className={`px-4 py-3 ${isRTL ? 'text-right' : ''}`}>
                        {toMillions(lead.estimatedValue, lead.currency) || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        /* Stats View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : ''}>
                  <p className="text-sm opacity-80">{txt.totalLeads}</p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </div>
                <Users className="w-10 h-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : ''}>
                  <p className="text-sm opacity-80">{txt.wonValue}</p>
                  <p className="text-2xl font-bold">${(stats.wonValue / 1000000).toFixed(1)}M</p>
                </div>
                <Trophy className="w-10 h-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : ''}>
                  <p className="text-sm opacity-80">{txt.pipelineValue}</p>
                  <p className="text-2xl font-bold">${(stats.totalValue / 1000000).toFixed(1)}M</p>
                </div>
                <TrendingUp className="w-10 h-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : ''}>
                  <p className="text-sm opacity-80">{txt.conversionRate}</p>
                  <p className="text-3xl font-bold">{stats.total > 0 ? Math.round((stats.won / stats.total) * 100) : 0}%</p>
                </div>
                <Percent className="w-10 h-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lead Details Dialog */}
      <Dialog open={showLeadDialog} onOpenChange={setShowLeadDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {editForm.type === 'company' ? <Building2 className="w-5 h-5 text-blue-500" /> : <Briefcase className="w-5 h-5 text-purple-500" />}
              {txt.leadDetails}
            </DialogTitle>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6">
              {/* Stage Progress Bar */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between overflow-x-auto pb-2">
                  {STAGES.filter(s => !['won', 'lost'].includes(s.id)).map((stage, idx, arr) => {
                    const currentStageIndex = arr.findIndex(s => s.id === editForm.status);
                    const isActive = stage.id === editForm.status;
                    const isCompleted = idx < currentStageIndex;
                    const isFuture = idx > currentStageIndex;
                    
                    return (
                      <React.Fragment key={stage.id}>
                        <div className={`flex flex-col items-center min-w-[80px] ${isRTL ? 'flex-col-reverse' : ''}`}>
                          <div 
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                              isActive ? 'ring-2 ring-offset-2' : ''
                            }`}
                            style={{ 
                              backgroundColor: isCompleted || isActive ? stage.color : 'hsl(var(--muted))',
                              color: isCompleted || isActive ? 'white' : 'hsl(var(--muted-foreground))',
                              ringColor: isActive ? stage.color : undefined
                            }}
                          >
                            {isCompleted ? <CheckCircle className="w-5 h-5" /> : <stage.icon className="w-5 h-5" />}
                          </div>
                          <span className={`text-xs mt-1 font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {language === 'ar' ? stage.labelAr : stage.label}
                          </span>
                        </div>
                        {idx < arr.length - 1 && (
                          <div className={`flex-1 h-1 mx-2 rounded ${idx < currentStageIndex ? 'bg-primary' : 'bg-muted'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                
                {/* Won/Lost badges if applicable */}
                {(editForm.status === 'won' || editForm.status === 'lost') && (
                  <div className="mt-3 flex justify-center">
                    <Badge 
                      className={`text-white px-4 py-1 ${editForm.status === 'won' ? 'bg-green-500' : 'bg-red-500'}`}
                    >
                      {editForm.status === 'won' ? <Trophy className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                      {STAGES.find(s => s.id === editForm.status)?.[language === 'ar' ? 'labelAr' : 'label']}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Current Status Display */}
              <div className="flex items-center gap-2 p-3 rounded-lg border" style={{ borderColor: STAGES.find(s => s.id === editForm.status)?.color }}>
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STAGES.find(s => s.id === editForm.status)?.color }} />
                <span className="font-medium">{language === 'ar' ? 'المرحلة الحالية:' : 'Current Stage:'}</span>
                <span style={{ color: STAGES.find(s => s.id === editForm.status)?.color }}>
                  {STAGES.find(s => s.id === editForm.status)?.[language === 'ar' ? 'labelAr' : 'label']}
                </span>
              </div>

              {/* Convert Opportunity to Project / Company (status >= contacted) */}
              {CONVERTIBLE_STATUSES.includes(editForm.status) && (
                <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3" data-testid="convert-panel">
                  <div className="flex items-center gap-2 flex-wrap">
                    <GitBranch className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">
                      {language === 'ar' ? 'تحويل إلى' : 'Convert to'}
                    </span>
                    <Badge variant="outline" className={`text-[10px] ml-auto ${isAdmin ? 'border-green-500 text-green-700 dark:text-green-400' : 'border-amber-500 text-amber-700 dark:text-amber-400'}`}>
                      {isAdmin
                        ? (language === 'ar' ? 'عام — مرئي لجميع المستخدمين' : 'Public — visible to all users')
                        : (language === 'ar' ? 'خاص — مرئي لك فقط' : 'Private — visible only to you')}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Convert to Project */}
                    <div className="flex items-center gap-2">
                      {editForm.convertedToProjectId ? (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-start gap-2"
                          onClick={() => navigate(`/dashboard/projects/${editForm.convertedToProjectId}`)}
                          data-testid="convert-view-project-btn"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          {language === 'ar' ? 'تم التحويل إلى مشروع — عرض' : 'Converted to Project — View'}
                          <ExternalLink className="w-3 h-3 ml-auto" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="default"
                          className="w-full justify-start gap-2"
                          onClick={() => handleConvertOpportunity('project')}
                          disabled={converting !== null}
                          data-testid="convert-to-project-btn"
                        >
                          {converting === 'project' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Briefcase className="w-4 h-4" />}
                          {language === 'ar' ? 'تحويل إلى مشروع' : 'Convert to Project'}
                        </Button>
                      )}
                    </div>
                    {/* Convert to Company */}
                    <div className="flex items-center gap-2">
                      {editForm.convertedToCompanyId ? (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-start gap-2"
                          onClick={() => navigate(`/dashboard/companies/${editForm.convertedToCompanyId}`)}
                          data-testid="convert-view-company-btn"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          {language === 'ar' ? 'تم التحويل إلى شركة — عرض' : 'Converted to Company — View'}
                          <ExternalLink className="w-3 h-3 ml-auto" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="default"
                          className="w-full justify-start gap-2"
                          onClick={() => handleConvertOpportunity('company')}
                          disabled={converting !== null}
                          data-testid="convert-to-company-btn"
                        >
                          {converting === 'company' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
                          {language === 'ar' ? 'تحويل إلى شركة' : 'Convert to Company'}
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {isAdmin
                      ? (language === 'ar'
                          ? 'سيتم إنشاء سجل عام في قسم المشاريع و/أو الشركات، مرئي لجميع المستخدمين. يتم نسخ الاسم والدولة والمدينة والصناعات والقيمة التقديرية والملاحظات تلقائيًا.'
                          : 'Creates a public record in Projects and/or Companies, visible to all users. Name, country, city, industries, estimated value and notes are copied automatically.')
                      : (language === 'ar'
                          ? 'سيتم إنشاء سجل خاص في قسم المشاريع و/أو الشركات، مرئي لك فقط (مميز بلون مختلف ووسم "من CRM"). يتم نسخ الاسم والدولة والمدينة والصناعات والقيمة التقديرية والملاحظات تلقائيًا.'
                          : 'Creates a private record in Projects and/or Companies, visible only to you (highlighted with a different color and a "From CRM" tag). Name, country, city, industries, estimated value and notes are copied automatically.')}
                  </p>
                </div>
              )}

              {/* Stage-Specific Mandatory Fields */}
              {MANDATORY_FIELDS[editForm.status] && (
                <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 text-amber-800 dark:text-amber-200 mb-3">
                    <AlertCircle className="w-4 h-4" />
                    {language === 'ar' ? 'حقول مطلوبة لهذه المرحلة' : 'Required Fields for This Stage'}
                  </h4>
                  
                  {/* NEW Stage Fields - Multiple Contacts */}
                  {editForm.status === 'new' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {language === 'ar' ? 'جهات الاتصال' : 'Contact Persons'} 
                          <span className="text-red-500 ml-1">*</span>
                          <span className="text-muted-foreground text-xs ml-2">
                            ({language === 'ar' ? 'مطلوب جهة اتصال واحدة على الأقل' : 'At least one required'})
                          </span>
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setEditForm(prev => ({
                            ...prev,
                            contactPersons: [...(prev.contactPersons || []), { name: '', email: '', phone: '', position: '' }]
                          }))}
                          data-testid="add-contact-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {language === 'ar' ? 'إضافة جهة اتصال' : 'Add Contact'}
                        </Button>
                      </div>
                      
                      {(editForm.contactPersons || []).map((contact, index) => (
                        <div key={index} className="p-3 border rounded-lg bg-background space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-muted-foreground">
                              {language === 'ar' ? `جهة الاتصال ${index + 1}` : `Contact ${index + 1}`}
                              {index === 0 && <span className="text-primary ml-2">({language === 'ar' ? 'أساسي' : 'Primary'})</span>}
                            </span>
                            {(editForm.contactPersons?.length || 0) > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                onClick={() => setEditForm(prev => ({
                                  ...prev,
                                  contactPersons: prev.contactPersons.filter((_, i) => i !== index)
                                }))}
                                data-testid={`remove-contact-${index}-btn`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs flex items-center gap-1">
                                {txt.contactName} {index === 0 && <span className="text-red-500">*</span>}
                              </Label>
                              <Input 
                                value={contact.name || ''} 
                                onChange={(e) => {
                                  const newContacts = [...(editForm.contactPersons || [])];
                                  newContacts[index] = { ...newContacts[index], name: e.target.value };
                                  setEditForm(prev => ({ ...prev, contactPersons: newContacts }));
                                }}
                                placeholder={language === 'ar' ? 'الاسم' : 'Name'}
                                data-testid={`contact-${index}-name-input`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs flex items-center gap-1">
                                {txt.email} {index === 0 && <span className="text-red-500">*</span>}
                              </Label>
                              <Input 
                                type="email"
                                value={contact.email || ''} 
                                onChange={(e) => {
                                  const newContacts = [...(editForm.contactPersons || [])];
                                  newContacts[index] = { ...newContacts[index], email: e.target.value };
                                  setEditForm(prev => ({ ...prev, contactPersons: newContacts }));
                                }}
                                placeholder={language === 'ar' ? 'البريد' : 'Email'}
                                data-testid={`contact-${index}-email-input`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs flex items-center gap-1">
                                {txt.phone} {index === 0 && <span className="text-red-500">*</span>}
                              </Label>
                              <Input 
                                value={contact.phone || ''} 
                                onChange={(e) => {
                                  const newContacts = [...(editForm.contactPersons || [])];
                                  newContacts[index] = { ...newContacts[index], phone: e.target.value };
                                  setEditForm(prev => ({ ...prev, contactPersons: newContacts }));
                                }}
                                placeholder={language === 'ar' ? 'الهاتف' : 'Phone'}
                                data-testid={`contact-${index}-phone-input`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">{txt.position}</Label>
                              <Input 
                                value={contact.position || ''} 
                                onChange={(e) => {
                                  const newContacts = [...(editForm.contactPersons || [])];
                                  newContacts[index] = { ...newContacts[index], position: e.target.value };
                                  setEditForm(prev => ({ ...prev, contactPersons: newContacts }));
                                }}
                                placeholder={language === 'ar' ? 'المنصب' : 'Position'}
                                data-testid={`contact-${index}-position-input`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* CONTACTED Stage Fields */}
                  {editForm.status === 'contacted' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'تم التسجيل' : 'Registration Completed'} <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex items-center gap-2 pt-2">
                          <Checkbox 
                            checked={editForm.registrationCompleted || false}
                            onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, registrationCompleted: checked }))}
                            data-testid="registration-completed-checkbox"
                          />
                          <span className="text-sm">{language === 'ar' ? 'تم استكمال التسجيل/التأهيل المسبق' : 'Registration/Prequalification completed'}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'موعد التأهيل المسبق' : 'Prequalification Deadline'} <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="date"
                          value={editForm.prequalDeadline ? editForm.prequalDeadline.split('T')[0] : ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, prequalDeadline: e.target.value }))}
                          data-testid="prequal-deadline-input"
                        />
                      </div>
                    </div>
                  )}

                  {/* QUALIFIED Stage Fields */}
                  {editForm.status === 'qualified' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'تم استلام دعوة المناقصة' : 'Bid Invitation Received'} <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex items-center gap-2 pt-2">
                          <Checkbox 
                            checked={editForm.bidInviteReceived || false}
                            onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, bidInviteReceived: checked }))}
                            data-testid="bid-invite-checkbox"
                          />
                          <span className="text-sm">{language === 'ar' ? 'تم استلام الدعوة للمناقصة' : 'Received invitation to bid'}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'تاريخ دعوة المناقصة' : 'Bid Invitation Date'} <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="date"
                          value={editForm.bidInviteDate ? editForm.bidInviteDate.split('T')[0] : ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, bidInviteDate: e.target.value }))}
                          data-testid="bid-invite-date-input"
                        />
                      </div>
                    </div>
                  )}

                  {/* OPPORTUNITY Stage Fields */}
                  {editForm.status === 'opportunity' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'موعد التقديم' : 'Submission Deadline'} <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="date"
                          value={editForm.submissionDeadline ? editForm.submissionDeadline.split('T')[0] : ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, submissionDeadline: e.target.value }))}
                          data-testid="submission-deadline-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {txt.estimatedValue} ($) <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="number"
                          value={editForm.estimatedValue || ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, estimatedValue: parseFloat(e.target.value) || 0 }))}
                          placeholder="0"
                          data-testid="estimated-value-input"
                        />
                      </div>
                    </div>
                  )}

                  {/* SUBMITTED Stage Fields */}
                  {editForm.status === 'submitted' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'تاريخ التقديم' : 'Submission Date'} <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="date"
                          value={editForm.submittedAt ? editForm.submittedAt.split('T')[0] : ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, submittedAt: e.target.value }))}
                          data-testid="submitted-at-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'قيمة العرض' : 'Proposal Value'} ($) <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="number"
                          value={editForm.proposalValue || ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, proposalValue: parseFloat(e.target.value) || 0 }))}
                          placeholder="0"
                          data-testid="proposal-value-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'تاريخ القرار المتوقع' : 'Expected Decision Date'} <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="date"
                          value={editForm.expectedDecisionDate ? editForm.expectedDecisionDate.split('T')[0] : ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, expectedDecisionDate: e.target.value }))}
                          data-testid="expected-decision-input"
                        />
                      </div>
                    </div>
                  )}

                  {/* WON Stage Fields */}
                  {editForm.status === 'won' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'تاريخ الفوز' : 'Won Date'} <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="date"
                          value={editForm.wonAt ? editForm.wonAt.split('T')[0] : ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, wonAt: e.target.value }))}
                          data-testid="won-at-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'قيمة العقد' : 'Contract Value'} ($) <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="number"
                          value={editForm.contractValue || ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, contractValue: parseFloat(e.target.value) || 0 }))}
                          placeholder="0"
                          data-testid="contract-value-input"
                        />
                      </div>
                    </div>
                  )}

                  {/* LOST Stage Fields */}
                  {editForm.status === 'lost' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'تاريخ الخسارة' : 'Lost Date'} <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          type="date"
                          value={editForm.lostAt ? editForm.lostAt.split('T')[0] : ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, lostAt: e.target.value }))}
                          data-testid="lost-at-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          {language === 'ar' ? 'سبب الخسارة' : 'Lost Reason'} <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          value={editForm.lostReason || ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, lostReason: e.target.value }))}
                          placeholder={language === 'ar' ? 'سبب عدم الفوز' : 'Reason for not winning'}
                          data-testid="lost-reason-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stage Transition Buttons */}
              {(getAllowedForwardStages(editForm.status).length > 0 || getAllowedBackwardStages(editForm.status).length > 0) && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-4">
                  {/* Move Forward Section */}
                  {getAllowedForwardStages(editForm.status).length > 0 && (
                    <div>
                      <h4 className="font-medium flex items-center gap-2 mb-3 text-green-700 dark:text-green-400">
                        <ArrowRight className="w-4 h-4" />
                        {language === 'ar' ? 'الانتقال إلى المرحلة التالية' : 'Move to Next Stage'}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {getAllowedForwardStages(editForm.status).map(nextStageId => {
                          const nextStage = STAGES.find(s => s.id === nextStageId);
                          const validation = validateStageFields(editForm, editForm.status);
                          
                          return (
                            <Button
                              key={nextStageId}
                              variant="outline"
                              className="flex items-center gap-2"
                              style={{ borderColor: nextStage?.color, color: nextStage?.color }}
                              onClick={async () => {
                                if (!validation.valid) {
                                  const fieldLabels = validation.missingFields.map(f => 
                                    f.label[language][MANDATORY_FIELDS[editForm.status].fields.indexOf(f.field)]
                                  ).join(', ');
                                  toast.error(
                                    language === 'ar' 
                                      ? `يرجى ملء الحقول المطلوبة: ${fieldLabels}`
                                      : `Please fill required fields: ${fieldLabels}`
                                  );
                                  return;
                                }
                                // Auto-save stage change with the FULL form so
                                // current-stage fields aren't lost on reload.
                                setEditForm(prev => ({ ...prev, status: nextStageId }));
                                try {
                                  const resp = await persistStageChange(nextStageId);
                                  if (resp.ok) {
                                    fetchLeads();
                                    toast.success(
                                      language === 'ar'
                                        ? `تم الانتقال إلى ${nextStage?.labelAr}`
                                        : `Moved to ${nextStage?.label}`
                                    );
                                  } else {
                                    toast.error(language === 'ar' ? 'فشل تحديث المرحلة' : 'Failed to move stage');
                                  }
                                } catch {
                                  toast.error(language === 'ar' ? 'فشل تحديث المرحلة' : 'Failed to move stage');
                                }
                              }}
                              data-testid={`move-to-${nextStageId}-btn`}
                            >
                              <nextStage.icon className="w-4 h-4" />
                              {language === 'ar' ? `انتقل إلى ${nextStage?.labelAr}` : `Move to ${nextStage?.label}`}
                              <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                            </Button>
                          );
                        })}
                      </div>
                      {!validateStageFields(editForm, editForm.status).valid && (
                        <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {language === 'ar' 
                            ? 'يجب ملء جميع الحقول المطلوبة أعلاه قبل الانتقال للمرحلة التالية'
                            : 'Fill all required fields above before moving to next stage'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Move Backward Section */}
                  {getAllowedBackwardStages(editForm.status).length > 0 && (
                    <div className={getAllowedForwardStages(editForm.status).length > 0 ? 'pt-3 border-t border-dashed' : ''}>
                      <h4 className="font-medium flex items-center gap-2 mb-3 text-muted-foreground">
                        <ArrowRight className={`w-4 h-4 rotate-180`} />
                        {language === 'ar' ? 'العودة إلى المرحلة السابقة' : 'Move Back to Previous Stage'}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {getAllowedBackwardStages(editForm.status).map(prevStageId => {
                          const prevStage = STAGES.find(s => s.id === prevStageId);
                          
                          return (
                            <Button
                              key={prevStageId}
                              variant="ghost"
                              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                              onClick={async () => {
                                // Persist the backward move too (was local-only,
                                // leaving the DB out of sync until Save).
                                setEditForm(prev => ({ ...prev, status: prevStageId }));
                                try {
                                  const resp = await persistStageChange(prevStageId);
                                  if (resp.ok) {
                                    fetchLeads();
                                    toast.info(
                                      language === 'ar'
                                        ? `تم العودة إلى ${prevStage?.labelAr}`
                                        : `Moved back to ${prevStage?.label}`
                                    );
                                  } else {
                                    toast.error(language === 'ar' ? 'فشل تحديث المرحلة' : 'Failed to move stage');
                                  }
                                } catch {
                                  toast.error(language === 'ar' ? 'فشل تحديث المرحلة' : 'Failed to move stage');
                                }
                              }}
                              data-testid={`move-back-to-${prevStageId}-btn`}
                            >
                              <ArrowRight className={`w-4 h-4 rotate-180`} />
                              <prevStage.icon className="w-4 h-4" />
                              {language === 'ar' ? `العودة إلى ${prevStage?.labelAr}` : `Back to ${prevStage?.label}`}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Basic Info (collapsed section) */}
              <details className="group" open>
                <summary className="cursor-pointer font-medium flex items-center gap-2 p-2 hover:bg-muted/50 rounded-lg">
                  <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                  {language === 'ar' ? 'معلومات أساسية' : 'Basic Information'}
                </summary>
                <div className="grid grid-cols-2 gap-4 mt-3 pl-6">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      {txt.leadName}
                      {editForm.status !== 'new' && <span className="text-xs text-muted-foreground">({language === 'ar' ? 'للقراءة فقط' : 'read-only'})</span>}
                    </Label>
                    <Input 
                      value={editForm.name || ''} 
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} 
                      disabled={editForm.status !== 'new'}
                      className={editForm.status !== 'new' ? 'bg-muted cursor-not-allowed' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      {txt.leadType}
                      {editForm.status !== 'new' && <span className="text-xs text-muted-foreground">({language === 'ar' ? 'للقراءة فقط' : 'read-only'})</span>}
                    </Label>
                    <Select value={editForm.type} onValueChange={(v) => setEditForm(prev => ({ ...prev, type: v }))} disabled={editForm.status !== 'new'}>
                      <SelectTrigger className={editForm.status !== 'new' ? 'bg-muted cursor-not-allowed' : ''}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company">{txt.company}</SelectItem>
                        <SelectItem value="project">{txt.project}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{txt.country}</Label>
                    <Select value={editForm.country} onValueChange={(v) => setEditForm(prev => ({ ...prev, country: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(countryFlags).map(code => (
                          <SelectItem key={code} value={code}>{countryFlags[code]} {countryNames[language][code]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{txt.city}</Label>
                    <Input value={editForm.city || ''} onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>{txt.website}</Label>
                    <Input value={editForm.website || ''} onChange={(e) => setEditForm(prev => ({ ...prev, website: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>{txt.priority}</Label>
                    <div className="flex items-center gap-1 pt-2">
                      {[1, 2, 3].map((star) => (
                        <Star
                          key={star}
                          className={`w-6 h-6 cursor-pointer ${star <= editForm.priority ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                          onClick={() => setEditForm(prev => ({ ...prev, priority: star }))}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{txt.probability} (%)</Label>
                    <Input type="number" min="0" max="100" value={editForm.probability || 0} onChange={(e) => setEditForm(prev => ({ ...prev, probability: parseInt(e.target.value) || 0 }))} />
                  </div>
                </div>
              </details>

              {/* Contact Persons (collapsed section) - only show if not in 'new' stage since it's mandatory there */}
              {editForm.status !== 'new' && (
                <details className="group">
                  <summary className="cursor-pointer font-medium flex items-center gap-2 p-2 hover:bg-muted/50 rounded-lg">
                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                    <Users className="w-4 h-4" />
                    {txt.contactInfo} ({(editForm.contactPersons || []).length})
                  </summary>
                  <div className="mt-3 pl-6 space-y-4">
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditForm(prev => ({
                          ...prev,
                          contactPersons: [...(prev.contactPersons || []), { name: '', email: '', phone: '', position: '' }]
                        }))}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {language === 'ar' ? 'إضافة جهة اتصال' : 'Add Contact'}
                      </Button>
                    </div>
                    
                    {(editForm.contactPersons || []).map((contact, index) => (
                      <div key={index} className="p-3 border rounded-lg bg-muted/20 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">
                            {language === 'ar' ? `جهة الاتصال ${index + 1}` : `Contact ${index + 1}`}
                            {index === 0 && <span className="text-primary ml-2">({language === 'ar' ? 'أساسي' : 'Primary'})</span>}
                          </span>
                          {(editForm.contactPersons?.length || 0) > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              onClick={() => setEditForm(prev => ({
                                ...prev,
                                contactPersons: prev.contactPersons.filter((_, i) => i !== index)
                              }))}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">{txt.contactName}</Label>
                            <Input 
                              value={contact.name || ''} 
                              onChange={(e) => {
                                const newContacts = [...(editForm.contactPersons || [])];
                                newContacts[index] = { ...newContacts[index], name: e.target.value };
                                setEditForm(prev => ({ ...prev, contactPersons: newContacts }));
                              }}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">{txt.position}</Label>
                            <Input 
                              value={contact.position || ''} 
                              onChange={(e) => {
                                const newContacts = [...(editForm.contactPersons || [])];
                                newContacts[index] = { ...newContacts[index], position: e.target.value };
                                setEditForm(prev => ({ ...prev, contactPersons: newContacts }));
                              }}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">{txt.email}</Label>
                            <Input 
                              type="email" 
                              value={contact.email || ''} 
                              onChange={(e) => {
                                const newContacts = [...(editForm.contactPersons || [])];
                                newContacts[index] = { ...newContacts[index], email: e.target.value };
                                setEditForm(prev => ({ ...prev, contactPersons: newContacts }));
                              }}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">{txt.phone}</Label>
                            <Input 
                              value={contact.phone || ''} 
                              onChange={(e) => {
                                const newContacts = [...(editForm.contactPersons || [])];
                                newContacts[index] = { ...newContacts[index], phone: e.target.value };
                                setEditForm(prev => ({ ...prev, contactPersons: newContacts }));
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {(editForm.contactPersons || []).length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {language === 'ar' ? 'لا توجد جهات اتصال. أضف جهة اتصال جديدة.' : 'No contacts yet. Add a new contact.'}
                      </p>
                    )}
                  </div>
                </details>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>{txt.notes}</Label>
                <Textarea value={editForm.notes || ''} onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} />
              </div>

              {/* Source Info */}
              {selectedLead.source && (
                <div className="text-xs text-muted-foreground p-3 bg-muted/20 rounded-lg">
                  <span className="font-medium">{txt.source}:</span> {selectedLead.source === 'news' ? 'Market News' : selectedLead.source === 'tender' ? 'Public Tenders' : selectedLead.source === 'project' ? 'Project' : selectedLead.source === 'company' ? 'Company' : 'Manual Entry'}
                  {selectedLead.sourceId && <span className="ml-2">(ID: {selectedLead.sourceId})</span>}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* WO-21 — Delete is offered only to the people the server will
                actually allow: the creator, their Team Admin, or the MID2BID
                operator. It used to be offered to everybody who could open the
                opportunity, which included every Team member. */}
            {selectedLead?.canDelete && (
              <Button variant="destructive" onClick={() => handleDeleteClick(selectedLead?.id)} disabled={isDeleting} data-testid="delete-lead-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                {txt.delete}
              </Button>
            )}
            {selectedLead && !selectedLead.canDelete && !selectedLead.isOwn && selectedLead.ownerName && (
              <p className="text-xs text-muted-foreground self-center" data-testid="lead-owner-note">
                {language === 'ar'
                  ? `أنشأها ${selectedLead.ownerName} — يمكن لصاحبها أو لمسؤول الفريق حذفها.`
                  : `Created by ${selectedLead.ownerName} — only they or their Team Admin can delete it.`}
              </p>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setShowLeadDialog(false)}>{txt.cancel}</Button>
            <Button onClick={handleSaveLead} disabled={savingLead} className="bg-primary" data-testid="save-lead-btn">
              <Save className="w-4 h-4 mr-2" />
              {txt.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Plus className="w-5 h-5 text-primary" />
              {txt.createLead}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{txt.leadName} *</Label>
              <Input value={newLeadForm.name} onChange={(e) => setNewLeadForm(prev => ({ ...prev, name: e.target.value }))} placeholder={language === 'ar' ? '\u0623\u062f\u062e\u0644 \u0627\u0633\u0645 \u0627\u0644\u0639\u0645\u064a\u0644 \u0627\u0644\u0645\u062d\u062a\u0645\u0644' : 'Enter lead name'} className="bg-background" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{txt.leadType}</Label>
                <Select value={newLeadForm.type} onValueChange={(v) => setNewLeadForm(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project">{txt.project}</SelectItem>
                    <SelectItem value="company">{txt.company}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{txt.country}</Label>
                <Select value={newLeadForm.country} onValueChange={(v) => setNewLeadForm(prev => ({ ...prev, country: v }))}>
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(countryFlags).map(code => (
                      <SelectItem key={code} value={code}>{countryFlags[code]} {countryNames[language][code]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{txt.city}</Label>
              <Input value={newLeadForm.city} onChange={(e) => setNewLeadForm(prev => ({ ...prev, city: e.target.value }))} className="bg-background" />
            </div>
            
            <div className="space-y-2">
              <Label>{txt.notes}</Label>
              <Textarea value={newLeadForm.notes} onChange={(e) => setNewLeadForm(prev => ({ ...prev, notes: e.target.value }))} rows={2} className="bg-background" />
              {!isAdmin && (
                <p className="text-xs text-muted-foreground" data-testid="lead-notes-no-links-hint">
                  {language === 'ar' ? 'لا يُسمح بالروابط في هذا الحقل.' : 'Links/URLs are not allowed in this field.'}
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>{txt.cancel}</Button>
            <Button onClick={handleCreateLead} disabled={savingLead || !newLeadForm.name.trim()} className="bg-primary" data-testid="create-lead-btn">
              <Plus className="w-4 h-4 mr-2" />
              {txt.createLead}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="delete-confirm-title">
              {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'ar' 
                ? 'هل أنت متأكد من حذف هذا العميل المحتمل؟ لا يمكن التراجع عن هذا الإجراء.'
                : 'Are you sure you want to delete this lead? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete} data-testid="delete-cancel-btn">
              {txt.cancel}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete} 
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm-btn"
            >
              {isDeleting ? (language === 'ar' ? 'جاري الحذف...' : 'Deleting...') : txt.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Opportunity Alert Dialog */}
      <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              {language === 'ar' ? 'تعيين تنبيه' : 'Set Alert'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'سيتم إعلامك عند أي تحديث لهذه الفرصة'
                : 'You\'ll be notified when this opportunity is updated'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Item being watched */}
            {alertTarget && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  {language === 'ar' ? 'مراقبة' : 'Watching'}
                </p>
                <p className="font-medium text-sm line-clamp-2">{alertTarget.title}</p>
              </div>
            )}
            
            {/* Alert Name */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'اسم التنبيه' : 'Alert Name'}</Label>
              <Input
                value={alertName}
                onChange={(e) => setAlertName(e.target.value)}
                placeholder={language === 'ar' ? 'اسم التنبيه...' : 'Alert name...'}
              />
            </div>
            
            {/* Frequency */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'تكرار الإشعارات' : 'Notification Frequency'}</Label>
              <Select value={alertFrequency} onValueChange={setAlertFrequency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="realtime">
                    {language === 'ar' ? 'فوري' : 'Real-time (Immediate)'}
                  </SelectItem>
                  <SelectItem value="daily">
                    {language === 'ar' ? 'ملخص يومي' : 'Daily Digest'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Notification Channels */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'قنوات الإشعار' : 'Notification Channels'}</Label>
              <div className="flex flex-wrap gap-2">
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    alertChannels.includes('in_app') ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setAlertChannels(prev => 
                    prev.includes('in_app') ? prev.filter(c => c !== 'in_app') : [...prev, 'in_app']
                  )}
                >
                  <Bell className="w-4 h-4" />
                  <span className="text-sm">{language === 'ar' ? 'التطبيق' : 'In-App'}</span>
                </div>
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    alertChannels.includes('email') ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setAlertChannels(prev => 
                    prev.includes('email') ? prev.filter(c => c !== 'email') : [...prev, 'email']
                  )}
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{language === 'ar' ? 'البريد' : 'Email'}</span>
                </div>
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    alertChannels.includes('push') ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setAlertChannels(prev => 
                    prev.includes('push') ? prev.filter(c => c !== 'push') : [...prev, 'push']
                  )}
                >
                  <Smartphone className="w-4 h-4" />
                  <span className="text-sm">{language === 'ar' ? 'المتصفح' : 'Push'}</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertDialog(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateOpportunityAlert} 
              disabled={savingAlert || !alertName.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {savingAlert ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'إنشاء التنبيه' : 'Create Alert'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
